/**
 * Componente atómico para el contenido del botón (texto)
 *
 * Maneja el renderizado del texto del botón, incluyendo la lógica
 * de hover text y medición de ancho máximo.
 */

import React from 'react';
import { cn } from '@/shared/utils/cn';
import { cva } from 'class-variance-authority';
import { ButtonColors } from '@/shared/types/types';
import {
  isButtonSlotSpreadLayout,
  type ButtonSlotLayout,
  type ButtonSlotSpreadSides,
} from '@/shared/types/button';

/**
 * Variantes de color de texto usando CVA
 */
const textColorVariants = cva('', {
  variants: {
    color: {
      primary: '',
      secondary: '',
      tertiary: '',
      destructive: '',
      link: '',
      ghost: '',
      soft: '',
    },
    textAlign: {
      left: 'ui-text-left',
      center: 'ui-text-center',
      right: 'ui-text-right',
    },
  },
  defaultVariants: {
    color: 'primary',
    textAlign: 'left',
  },
});

export interface ButtonContentProps {
  /** Contenido a mostrar */
  displayText: React.ReactNode;
  /** Si hay leftSlot o rightSlot */
  hasSlots: boolean;
  /** Si hay badge inline tras el texto (evita `ui-w-full` que empuja el badge fuera con overflow) */
  hasTrailingBadge?: boolean;
  /** Modo de layout de slots (solo afecta clases cuando `spreadSides` está definido) */
  slotLayout?: ButtonSlotLayout;
  /** Qué slots hay en modo `spread`; si es `undefined`, el texto usa el layout inline clásico */
  spreadSides?: ButtonSlotSpreadSides | null;
  /** Estilo inline para el texto */
  textStyle?: React.CSSProperties;
  /** Color del texto */
  textColor?: ButtonColors;
  /** Alineación del texto */
  textAlign?: 'left' | 'center' | 'right';
  /** Referencia al elemento de medición */
  measureRef: React.RefObject<HTMLSpanElement>;
  /** Referencia al contenedor del texto / contenido */
  textRef: React.RefObject<HTMLDivElement>;
  /** Clases para el span de medición */
  measureSpanClassName: string;
}

/**
 * Renderiza el contenido de texto del botón
 *
 * @example
 * ```tsx
 * <ButtonContent
 *   displayText="Click me"
 *   hasSlots={false}
 *   textColor="primary"
 *   textAlign="center"
 *   measureRef={measureRef}
 *   textRef={textRef}
 *   measureSpanClassName={measureSpanClassName}
 * />
 * ```
 */
export const ButtonContent: React.FC<ButtonContentProps> = ({
  displayText,
  hasSlots,
  hasTrailingBadge = false,
  slotLayout = 'inline',
  spreadSides = null,
  textStyle,
  textColor,
  textAlign,
  measureRef,
  textRef,
  measureSpanClassName,
}) => {
  /* En botón solo es válido contenido fraseado: `<p>` dentro de `<button>` es HTML inválido y el motor repara el DOM (layout roto). */
  const spreadActive = isButtonSlotSpreadLayout(slotLayout) && spreadSides !== null;
  const spreadObjectAlign = isButtonSlotSpreadLayout(slotLayout) ? slotLayout.textAlign : undefined;
  const resolvedAlign =
    textAlign ??
    (spreadActive
      ? (spreadObjectAlign ??
        (spreadSides === 'both' ? 'center' : spreadSides === 'left-only' ? 'right' : 'left'))
      : 'center');

  return (
    <>
      {/* Span invisible para medir el ancho de los textos */}
      <span
        ref={measureRef}
        className={measureSpanClassName}
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
      />
      <div
        ref={textRef}
        className={cn(
          'ui-relative ui-leading-none',
          spreadActive
            ? 'ui-min-w-0 ui-flex-1'
            : hasSlots || hasTrailingBadge
              ? 'ui-min-w-0 ui-flex-1'
              : 'ui-w-full',
          /* `text-align` no centra hijos `display:flex`; CTA con icono + JSX necesita flex + ocupar la franja del botón */
          resolvedAlign === 'center' && !hasSlots && 'ui-flex ui-items-center ui-justify-center',
          spreadActive &&
            resolvedAlign === 'center' &&
            'ui-flex ui-items-center ui-justify-center ui-text-center',
          spreadActive &&
            resolvedAlign === 'right' &&
            'ui-flex ui-items-center ui-justify-end ui-text-right',
          spreadActive &&
            resolvedAlign === 'left' &&
            'ui-flex ui-items-center ui-justify-start ui-text-left',
          textColorVariants({ color: textColor, textAlign: resolvedAlign })
        )}
        style={textStyle}
      >
        {typeof displayText === 'string' || typeof displayText === 'number' ? (
          <>{displayText} </>
        ) : (
          displayText
        )}
      </div>
    </>
  );
};

ButtonContent.displayName = 'ButtonContent';
